<script setup>
/**
 * @description 页脚第三方社媒入口（PC 气泡 / 移动端点击）
 * @param {'desktop'|'mobile'} variant - 展示形态
 */

import { ref } from 'vue'
import BasePopover from '~/components/base/Popover.vue'
import FooterSocialQrPanel from '~/components/base/footer/FooterSocialQrPanel.vue'
import MobilePopup from '~/components/mobile/Popup.vue'
import { FOOTER_SOCIAL_ITEMS } from '~/utils/footerSocials'

const props = defineProps({
  variant: {
    type: String,
    default: 'desktop',
    validator: (value) => ['desktop', 'mobile'].includes(value),
  },
})

const { withBase } = useBasePath()
const mobileQrVisible = ref(false)
const mobileQrItem = ref(null)
const hoveredSocialKey = ref(null)

/**
 * @description 解析社媒图标地址（桌面端 hover 时切换彩色图标）
 * @param {import('~/utils/footerSocials').FooterSocialItem} item
 * @returns {string}
 */
const resolveSocialIcon = (item) => {
  const iconPath =
    hoveredSocialKey.value === item.key && item.iconHover ? item.iconHover : item.icon
  return withBase(iconPath)
}

/**
 * @description 桌面端鼠标移入社媒按钮
 * @param {import('~/utils/footerSocials').FooterSocialItem} item
 * @returns {void}
 */
const handleSocialMouseEnter = (item) => {
  if (props.variant !== 'desktop') return
  hoveredSocialKey.value = item.key
}

/**
 * @description 桌面端鼠标移出社媒按钮
 * @returns {void}
 */
const handleSocialMouseLeave = () => {
  hoveredSocialKey.value = null
}

/**
 * @description 桌面端二维码气泡显隐时同步图标高亮
 * @param {boolean} visible
 * @param {import('~/utils/footerSocials').FooterSocialItem} item
 * @returns {void}
 */
const handleQrPopoverVisibleChange = (visible, item) => {
  if (props.variant !== 'desktop') return
  hoveredSocialKey.value = visible ? item.key : null
}

/**
 * @description 打开移动端二维码抽屉
 * @param {import('~/utils/footerSocials').FooterSocialItem} item
 * @returns {void}
 */
const openMobileQr = (item) => {
  mobileQrItem.value = item
  mobileQrVisible.value = true
}

/**
 * @description 关闭移动端二维码抽屉
 * @returns {void}
 */
const closeMobileQr = () => {
  mobileQrVisible.value = false
}
</script>

<template>
  <div
    class="footer-socials"
    :class="{
      'footer-socials--desktop': props.variant === 'desktop',
      'footer-socials--mobile': props.variant === 'mobile',
    }"
    aria-label="社交媒体"
  >
    <template v-for="item in FOOTER_SOCIAL_ITEMS" :key="item.key">
      <a
        v-if="item.action === 'link'"
        :aria-label="item.label"
        :href="item.href"
        class="footer-socials__button"
        rel="noopener noreferrer"
        target="_blank"
        @mouseenter="handleSocialMouseEnter(item)"
        @mouseleave="handleSocialMouseLeave"
      >
        <span class="footer-socials__icon-wrap">
          <img :src="resolveSocialIcon(item)" alt="" class="footer-socials__icon" />
        </span>
      </a>

      <template v-else-if="props.variant === 'desktop'">
        <BasePopover
          position="top"
          trigger="hover"
          :content-style="{ padding: '16px 20px' }"
          :overlay-style="{ zIndex: 1000 }"
          :auto-fit-position="true"
          :mouse-enter-delay="80"
          :mouse-leave-delay="200"
          @popup-visible-change="(visible) => handleQrPopoverVisibleChange(visible, item)"
        >
          <button :aria-label="item.label" type="button" class="footer-socials__button">
            <span class="footer-socials__icon-wrap">
              <img :src="resolveSocialIcon(item)" alt="" class="footer-socials__icon" />
            </span>
          </button>
          <template #content>
            <FooterSocialQrPanel :qr-image="item.qrImage" :qr-text="item.qrText" />
          </template>
        </BasePopover>
      </template>

      <button
        v-else
        :aria-label="item.label"
        type="button"
        class="footer-socials__button"
        @click="openMobileQr(item)"
      >
        <span class="footer-socials__icon-wrap">
          <img :src="withBase(item.icon)" alt="" class="footer-socials__icon" />
        </span>
      </button>
    </template>

    <MobilePopup
      v-if="props.variant === 'mobile'"
      v-model:visible="mobileQrVisible"
      :title="mobileQrItem?.label || ''"
      @close="closeMobileQr"
    >
      <div v-if="mobileQrItem" class="footer-socials__mobile-qr">
        <FooterSocialQrPanel :qr-image="mobileQrItem.qrImage" :qr-text="mobileQrItem.qrText" />
      </div>
    </MobilePopup>
  </div>
</template>

<style scoped lang="scss">
.footer-socials {
  display: flex;
  align-items: center;

  &--desktop {
    justify-content: flex-end;
    gap: $spacing-xl;
    flex-shrink: 0;
  }

  &--mobile {
    gap: $spacing-xl;
  }

  &__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    text-decoration: none;
    transition: opacity 0.2s ease;

    &:active {
      opacity: 0.7;
    }
  }

  &__icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 20px;
    flex-shrink: 0;
  }

  &__icon {
    display: block;
    width: auto;
    height: 20px;
    max-width: 100%;
    object-fit: contain;
  }

  &__mobile-qr {
    display: flex;
    justify-content: center;
    padding-top: $spacing-xs;
  }
}
</style>
